import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  MessageSquareText,
  Briefcase,
  CalendarDays,
  MapPin,
} from "lucide-react";
import axios from "axios";

const BidAccepted = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const {
    price,
    workerName = "Your Worker",
    jobTitle = "the job",
    scheduledAt,
    jobId,
    location = "your selected location",
  } = state || {};

  const [jobData, setJobData] = useState(null);

  // ✅ Pull the base API URL from .env
  const API_BASE = process.env.REACT_APP_API_BASE;

  // ✅ Fetch full job info so we can get customerId and assignedTo
  useEffect(() => {
    if (!jobId) return;
    const fetchJob = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/customers/jobs/job/${jobId}`);
        setJobData(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch job details:", err);
      }
    };
    fetchJob();
  }, [jobId, API_BASE]);

  const formattedDate = scheduledAt
    ? new Date(scheduledAt).toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "Date TBD";

  const customerId = jobData?.customerId?._id || jobData?.customerId;
  const workerId = jobData?.assignedTo?._id || jobData?.assignedTo;
  const chatJobId = jobData?._id || jobId;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-blue-50 p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="bg-white/80 backdrop-blur-lg border border-green-100 shadow-2xl rounded-3xl max-w-2xl w-full p-10 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 150, delay: 0.3 }}
          className="flex justify-center mb-6"
        >
          <CheckCircle2
            className="text-green-600 w-20 h-20 drop-shadow-lg"
            strokeWidth={1.5}
          />
        </motion.div>

        <h1 className="text-4xl font-bold text-green-700 mb-2">
          Bid Accepted 🎉
        </h1>
        <p className="text-gray-700 text-lg mb-6">
          You’ve successfully assigned{" "}
          <span className="font-semibold text-green-800">{workerName}</span>{" "}
          for <span className="font-semibold text-blue-700">{jobTitle}</span>.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-white via-gray-50 to-green-50 rounded-2xl border border-gray-200 shadow-sm p-6 text-left space-y-4"
        >
          <div className="flex items-center gap-3 text-gray-700">
            <Briefcase className="text-green-600" size={20} />
            <p>
              <span className="font-semibold">Job ID:</span> {chatJobId}
            </p>
          </div>

          <div className="flex items-center gap-3 text-gray-700">
            <MessageSquareText className="text-blue-600" size={20} />
            <p>
              <span className="font-semibold">Accepted Bid:</span> ${price}
            </p>
          </div>

          <div className="flex items-center gap-3 text-gray-700">
            <CalendarDays className="text-green-600" size={20} />
            <p>
              <span className="font-semibold">Scheduled For:</span>{" "}
              {formattedDate}
            </p>
          </div>

          <div className="flex items-center gap-3 text-gray-700">
            <MapPin className="text-red-500" size={20} />
            <p>
              <span className="font-semibold">Location:</span> {location}
            </p>
          </div>
        </motion.div>

        <p className="text-gray-600 mt-6 leading-relaxed">
          The worker has been notified and will reach out soon. <br />
          Payment will only be processed{" "}
          <span className="font-semibold text-gray-800">
            after job completion
          </span>
          .
        </p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button
            disabled={!customerId || !workerId || !chatJobId}
            onClick={() =>
              navigate(`/chat/${customerId}/${workerId}/${chatJobId}`, { state })
            }
            className={`flex items-center justify-center gap-2 ${
              customerId && workerId
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            } font-medium py-3 px-6 rounded-xl shadow-md transition-all duration-200 hover:shadow-lg`}
          >
            <MessageSquareText size={18} /> Message Your Worker
          </button>

          <button
            onClick={() => navigate("/your-jobs")}
            className="flex items-center justify-center gap-2 bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 font-medium py-3 px-6 rounded-xl shadow-sm transition-all duration-200"
          >
            <Briefcase size={18} /> Go to Your Jobs
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default BidAccepted;
